/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2018, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'underscore',
	'../../util/AjaxHelper',
	'../../lib/@waca/dashboard-common/dist/utils/ContentUtil',
	'../../cmHelper/CMHelper',
	'../../widgets/livewidget/nls/StringResources',
	'../../features/content/common/DataPointActionBase'
], function(_, AjaxHelper, ContentUtil, CMHelper, StringResources, DataPointActionBase) {
	/**
	*
	* Handle 'Jump to' target, i.e, open target perspective
	**/
	class JumpToTarget extends DataPointActionBase {
		constructor(options) {
			super(options);
			this.drillDefinitionEntry = options.drillDefinitionEntry;
			this.selections = options.selections; //undefined at selected widget level, has valid data for selected data point

			this.rangeValueTypes = {
				boundRange: 'boundRange',
				unboundedRange: 'unboundedRange',
				unboundedStartRange: 'unboundedStartRange',
				unboundedEndRange: 'unboundedEndRange'
			};

			this.negativeFilterOps = ['notin', 'notbetween'];

			this.rangeFilterOps = ['gt', 'gte', 'lt', 'lte', 'between'];
		}

		getId() {
			return this.drillDefinitionEntry.getId();
		}

		getName() {
			return this.drillDefinitionEntry.getName();
		}

		jumpTo() {
			return this._buildDrillTargetSpec()
				.then((spec) => {
					spec = spec || {};
					spec.content = spec.content || {};
					spec.content.cmProperties = spec.content.cmProperties || {};
					const unsaveMessage = StringResources.get('drillthrough_unsaveMsg', { name: decodeURIComponent(spec.content.pathRef) });
					let openPerspective, closePerspective;
					openPerspective = closePerspective = this.drillDefinitionEntry.getPerspective();
					if(openPerspective === 'authoring') {
						// CADBLW-5575 - For reporting, we must not set the perspective in the
						// openApplication call because it prevents it from switching to ClassicViewer
						// in the case that the target report has interactivity turned off.
						openPerspective = undefined;
					}
					return this.dashboard.closeApplication(closePerspective, spec.content.cmProperties.id, { unsaveMessage: unsaveMessage })
						.then(this.dashboard.openApplication.bind(this.dashboard, openPerspective, spec));
				}).catch((error) => {
					const jqXHR = error.jqXHR || error || {};

					if (error.code === 404) {
						jqXHR.responseText = StringResources.get('drillthrough_jumpToTarget_notFound', { name: this.drillDefinitionEntry.getName() });
					} else {
						jqXHR.responseText = jqXHR.responseText ? jqXHR.responseText : jqXHR.statusText;
					}

					if (jqXHR.responseText) {
						AjaxHelper.showAjaxServiceErrorMessage(this.dashboard, jqXHR);
						//Eat the rejection and return as success
						return Promise.resolve();
					}

					// Unexpected case
					return this.dashboard.getGlassSvc('.Content').then((contentService) => {
						return CMHelper.getReportName(contentService, this.drillDefinitionEntry.getAssetId()).then((targetName) => {
							jqXHR.responseText = StringResources.get('errorDrillToTarget', { targetName: targetName });
							AjaxHelper.showAjaxServiceErrorMessage(this.dashboard, jqXHR);
							return Promise.resolve();
						});
					});

				});
		}

		_buildDrillTargetSpec() {
			const options = {};
			this.dashboard.prepareGlassOptions(options);
			const currentAssetId = this.drillDefinitionEntry.getAssetId();
			const currentSearchPath = this.drillDefinitionEntry.getAssetSearchPath();
			return CMHelper.getPathRefInfo(options, {
				storeId: currentAssetId,
				searchPath: currentSearchPath,
				fields: ['searchPath']
			}).then((pathRefInfo) => {
				if (!pathRefInfo) {
					return {};
				}
				const newAssetId = pathRefInfo.objRef;
				if (!_.isEmpty(newAssetId) && newAssetId !== currentAssetId) {
					this.drillDefinitionEntry.setAssetId(newAssetId);
				}
				const newAssetSearchPath = pathRefInfo.searchPath;
				if (!_.isEmpty(newAssetSearchPath) && newAssetSearchPath !== currentSearchPath) {
					this.drillDefinitionEntry.setAssetSearchPath(newAssetSearchPath);
				}

				const spec = {
					content: {
						cmProperties: {
							id: newAssetId
						},
						isViewer: true,
						altDrillFlag : true, //@todo Reporting add this new flag to route to new implementation that supports JSON parameter values
						prompt: false, //@todo what is this?
						format: 'HTML', // Default to HTML until we support more format.
						editSpecification: '', //dashboard drill through to target does not need this property
						pathRef: pathRefInfo.pathRef
					}
				};

				// exploration is the CM type for dashboard/story/explore
				if( this.drillDefinitionEntry.getType() === 'exploration'){
					spec.content.filters = JSON.stringify(this._getDashboardParameterValues());
				} else {
					spec.content.parameterValuesJSON = JSON.stringify(this._getReportParameterValues());
				}

				return spec;
			});
		}

		_getDashboardParameterValues(){
			if (!this.dashboardParameterValues) {
				this.dashboardParameterValues = this._getMergedConvertedFilter();
			}
			return this.dashboardParameterValues;
		}

		_getMergedConvertedFilter() {
			const { globalFilters, localFilters, selectionFilters, dataPointFilter  } = this._getFilterInfo();
			let mergedFilters = {};
			// the order has to be globalFilters -> localFilters -> dataPointFilter -> selectionFilters
			this._getSimpleFilterContext(globalFilters, mergedFilters);
			this._getSimpleFilterContext(localFilters, mergedFilters);
			this._getSimpleFilterContext(dataPointFilter, mergedFilters);
			this._getSimpleFilterContext(selectionFilters, mergedFilters);
			return Object.values(mergedFilters);
		}

		/**
		 * If the filter entry in filter list is simple expression, just add to mergedFilters.
		 * In case of compound filter express, walk each simple filter expression to collect the context.
		 * @extends Please check unit test {@link testJumpToDashboardWithComplicatedFilterExpression}
		 */
		_getSimpleFilterContext(filterList = [], mergedFilters, append = false) {
			if(filterList.length === 0) {
				return;
			}

			filterList.forEach(filter => {
				if(filter.columnId) {
					if (append && mergedFilters[filter.columnId] && mergedFilters[filter.columnId].values && filter.values) {
						// Filter column already exists in merged, just append the filter values.
						mergedFilters[filter.columnId].values.push(...filter.values);
					} else {
						mergedFilters[filter.columnId] = filter;
						mergedFilters[filter.columnId].assetId = this._getVisualization().getDataSource().getAssetId();
					}
				} else {
					// In case of compound filter, need to add the simple filter context or append the filter values if column id already exists
					this._getSimpleFilterContext(filter.values, mergedFilters, true);
				}
			});
		}

		_getFilterInfo() {
			const visualization = this._getVisualization();
			const datasource = visualization.getDataSource();
			const localFilters = visualization.getLocalFilters();
			const type = this.dashboard.getAppConfig('pageContainerType');

			const filterInfo = {
				localFilters: localFilters.getFilterList(),
				globalFilters: this.globalFilters.getFilterList({
					origin: 'filter',
					sourceId: datasource.getId(),
					scope: ContentUtil.getPageContent(this.content, type).getId(),
					eventGroupId: this.eventGroups.getGroupId(this.content.getId())
				}),
				selectionFilters: this.globalFilters.getFilterList({
					origin: 'visualization',
					sourceId: datasource.getId(),
					scope: ContentUtil.getPageContent(this.content, type).getId(),
					eventGroupId: this.eventGroups.getGroupId(this.content.getId())
				}),
				dataPointFilter: this._getDataPointFilter()
			};


			return filterInfo;
		}

		_getDataPointFilter(){
			const datapointFilters = [];
			if(this.selections && this.selections.dataPoints){
				this.selections.dataPoints.forEach(dataPoint => {
					if(dataPoint.categories){
						dataPoint.categories.forEach(category => {
							let filterEntry = _.findWhere(datapointFilters, { columnId: category.columnId });
							if (!filterEntry){
								filterEntry = {
									columnId: category.columnId,
									operator:'in',
									values: []
								};
								datapointFilters.push(filterEntry);
							}
							filterEntry.values.push({ u: category.value, d:category.label });

						});
					}
				});
			}
			return datapointFilters;
		}


		_getReportParameterValues() {
			let reportParameterValues = {};
			const mappings = this.drillDefinitionEntry.getMappings();
			_.each(mappings, (mapping) => {
				let values = this._getParamterValues(mapping);
				if (!values) {
					return;
				}

				if (this._isRangeParameter(mapping.capabilities)) {
					// Complex parameter values.
					const complexValues = this._buildRangeValues(values, mapping.type);
					reportParameterValues[mapping.name] = complexValues;
				} else {
					if (values.startValue || values.endValue) {
						// Dashboard has range values, and convert to simple array values
						const arrayValues = [];
						if (values.startValue) {
							arrayValues.push(values.startValue);
						}
						if (values.endValue) {
							arrayValues.push(values.endValue);
						}
						values = arrayValues;
					}
					if (values.length > 0) {
						// Simple parameter values built from simple array values.
						reportParameterValues[mapping.name] = [];
						values.forEach((value) => {
							reportParameterValues[mapping.name].push({
								use: this._getUseValueFromValue(value, mapping.type),
								display: this._getDisplayValueFromValue(value)
							});
						});
					}
				}

			});
			return reportParameterValues;
		}

		_getParamterValues(mapping) {
			let values;
			if (mapping && mapping.mapTo) {

				// 1) Get values from selections
				values = this._getValuesFromSelections(mapping.mapTo);

				// 2) No values from selections, search local filters
				if (!values || !values.length) {
					values = this._getValuesFromFilters(mapping.mapTo, this._getLocalFilters().getFilterList());
				}

				// 3) No values from projections and local filters, search global filters
				// in_range prompt expecting start and end values respectively so values is object type.
				if (!values|| (Array.isArray(values) && !values.length) || (typeof values === 'object' && _.isEmpty(values))) {
					let globalFilterList = [...this.globalFilters.getFilterList(this._getNetPageContextSelectors('filter'))];
					// Has no selection from 1) from this widget, but there might be filters triggered by other vis selections
					globalFilterList.push(...this.globalFilters.getFilterList(this._getNetPageContextSelectors('visualization')));
					values = this._getValuesFromFilters(mapping.mapTo, globalFilterList);
				}
			}

			return values;
		}

		_getValuesFromSelections(mapTo) {
			let values = null;

			// When selection has numeric values, map it to the target first since numeric selections are not included in pending filter or brushing
			let dataPoints = this.selections && this.selections.dataPoints || [];
			if (!dataPoints.length) {
				// Content contribution is built before selection happens so need to collect selections here.
				dataPoints = this.content.getFeature('DataPointSelections').getSelections();
			}

			const facts = _.pluck(dataPoints, 'facts');
			facts.forEach((items) => {
				items && items.forEach(item => {
					if (item.columnId === mapTo) {
						values = item.value !== null && item.value !== undefined ? [item.value] : null;
					}
				});
			});
			if (values) {
				// Found source column and return;
				return values;
			}

			const groupedSelections = this._groupSelections(dataPoints);
			values = this._getValues(groupedSelections, mapTo);

			// PH27077: When multi selection happens on multiple widgets, a possible case is this.selections has pending selections against widgetA, but the selected content
			// in canvas is a different widgetB, drill to target should pass selections in selected content as well.
			const selectedContents = this.dashboard.getCanvas().getSelectedContentList();
			const selectedContent = selectedContents.length === 1 ? selectedContents[0] : null;
			if (!selectedContent || selectedContent  === this.content) {
				// Don't need to get selections from selected content when there is no selected content 
				// or there are more than one selected content or the selected content is the current content.
				return values;
			}
			const selector = this._getNetPageContextSelectors('visualization');
			selector.eventSourceId = selectedContent.getId();
			const selectedContentSelections = this._getValuesFromFilters(mapTo, this.globalFilters.getFilterList(selector));
			if (selectedContentSelections && selectedContentSelections.length) {
				values = values || [];
				values.push(...selectedContentSelections);
			}

			return this._uniqueValues(values);
		}

		_getValues(groupedSelections, mapTo) {
			if (groupedSelections) {
				let values = [];
				for (let groupKey in groupedSelections) {
					const columnIds = groupKey.split(',');
					if (columnIds.includes(mapTo)) {
						groupedSelections[groupKey].forEach((groupSelection) => {
							groupSelection.forEach((selection) => {
								selection.columnId === mapTo && values.push(selection);
							});
						});
					}
				}
				return this._uniqueValues(values);
			}
		}

		_uniqueValues(values = []) {
			let uniqueValues = [];
			let valuesMap = {};
			values.forEach(value => {
				if (!valuesMap[value.u]) {
					valuesMap[value.u] = 1;
					uniqueValues.push(value);
				}
			});
			return uniqueValues;
		}

		_getFilterSpec(mapTo, filterList, resultSpec = null) {
			/**
			 * filterList could have multiple filters based on different pre or post aggregations or aggregation types
			 * But we will only pass the values of the first entry to target
			 */
			for (let i = 0; i < filterList.length; i++) {
				if (filterList[i].columnId === mapTo) {
					if (resultSpec) {
						resultSpec.values.push(...filterList[i].values);
					} else {
						resultSpec = filterList[i];
					}
					break;
				} else if (filterList[i].values && filterList[i].values.length) {
					resultSpec = this._getFilterSpec(mapTo, filterList[i].values, resultSpec);
				}
			}
			return resultSpec;
		}

		_getValuesFromFilters(mapTo, filterList) {
			const filterSpec = filterList && this._getFilterSpec(mapTo, filterList);

			// Return filter values if exist, and the operator is not negative operations
			// Fact filter ids are generated from itemId and aggregation type, e.g. [Sales (query)].[Sales].[Revenue]_sum,
			// so get(mapTo) won't return fact filters.
			let values;
			if (filterSpec && !this._isNegativeOperation(filterSpec) && filterSpec.values) {
				values = filterSpec.values;
				if (this._isRangeFilter(filterSpec)) {
					let startValue;
					let endValue;
					switch(filterSpec.operator) {
						case 'between':
							startValue = values[0];
							endValue = values[1];
							break;
						case 'gt':
						case 'gte':
							startValue = values[0];
							break;
						case 'lt':
						case 'lte':
							endValue = values[0];
							break;
						default:
							break;
					}

					// complex value structure
					values = {
						startValue: startValue,
						endValue: endValue
					};
				}
			}
			return values;
		}

		_getNetPageContextSelectors(origin) {
			const page = ContentUtil.getPageContent(this.content);
			return {
				scope: page && page.getId(),
				sourceId: this._getVisualization().getDataSource().getId(),
				origin: origin
			};
		}

		_isNegativeOperation(localFilter) {
			return this.negativeFilterOps.indexOf(localFilter.operator) !== -1;
		}

		_isRangeFilter(localFilter) {
			return this.rangeFilterOps.indexOf(localFilter.operator) !== -1;
		}

		remove() {
			this.drillDefinitionEntry = null;
			this.selections = null;
		}

		_isRangeParameter(capabilities) {
			return capabilities &&
					(capabilities.indexOf(this.rangeValueTypes.unboundedRange) !== -1 ||
					capabilities.indexOf(this.rangeValueTypes.boundRange) !== -1);
		}

		/**
		 * Range parameter value is a complex expression with the
		 * form of "boundRange" with a "start" and "end", "unboundedEndRange" with a "start" and "unboundedStartRange" with an "end",
		 * and the values are from selections or a range filters. The first value will be used for both tart and end in case of multi selected values.
		 * @example
		 * {
		 *	'Parameter1': [
		 *		{
		 *		'boundRange': {
		 *			'start': {
		 *			'use': '2010-01-01T00:00:00',
		 *			'display': '2010-01-01T00:00:00'
		 *			},
		 *			'end': {
		 *			'use': '2010-01-31T00:00:00',
		 *			'display': '2010-01-31T00:00:00'
		 *			}
		 *		}
		 *		}
		 *	]
		 *	}
		 * @example
		 * {
		 *	'Parameter1': [
		 *		{
		 *		'unboundedEndRange': {
		 *			'start': {
		 *			'use': '2013-07-01T00:00:00',
		 *			'display': '2013-07-01T00:00:00'
		 *			}
		 *		}
		 *		}
		 *	]
		 *	}
		 * @param {object} values - either an array of values or an object with start and end values.
		 * @param {string} mappingType - the parameter type.
		 */
		_buildRangeValues(values, mappingType) {
			let startValue;
			let endValue;

			if (values) {
				// For non-range selected values, set start and end values to the first selected value.
				if (_.isArray(values) && values.length >= 1) {
					startValue = values[0];
					endValue = values[0];
				} else {
					startValue = values.startValue;
					endValue = values.endValue;
				}
			}

			const valueExpr = {};
			if (startValue && endValue) {
				valueExpr[this.rangeValueTypes.boundRange] = {
					start: {
						use: this._getUseValueFromValue(startValue, mappingType),
						display: this._getDisplayValueFromValue(startValue)
					},
					end:  {
						use: this._getUseValueFromValue(endValue, mappingType),
						display: this._getDisplayValueFromValue(endValue)
					}
				};
			} else if (startValue) {
				valueExpr[this.rangeValueTypes.unboundedEndRange] = {
					start: {
						use: this._getUseValueFromValue(startValue, mappingType),
						display: this._getDisplayValueFromValue(startValue)
					}
				};
			} else if (endValue) {
				valueExpr[this.rangeValueTypes.unboundedStartRange] = {
					end:  {
						use: this._getUseValueFromValue(endValue, mappingType),
						display: this._getDisplayValueFromValue(endValue)
					}
				};
			}

			// When a range has no startValue and endValue return null.
			return _.isEmpty(valueExpr) ? null : [valueExpr];
		}

		_getUseValueFromValue(value, mappingType) {
			let resultValue = null;
			if (typeof value === 'object') {
				if (mappingType === 'memberUniqueName') {
					resultValue = value.value === undefined ? value.u : value.value;
				} else {
					resultValue = value.label === undefined ? value.d : value.label;
				}
			} else {
				resultValue = value;
			}
			return  resultValue;
		}

		_getDisplayValueFromValue(value) {
			return typeof value === 'object' ? (value.label === undefined ? value.d : value.label) : value;
		}
	}

	return JumpToTarget;
});
